<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import type { ContentPreview, Service, TransactionDetail } from "@/types";
import { useTransactionBody } from "@/queries/runs";
import KeysetPager from "@/components/KeysetPager.vue";
import TransactionBlock from "./TransactionBlock.vue";
import TransactionField from "./TransactionField.vue";
import TransactionBody from "./TransactionBody.vue";
import TransactionMessageView from "./TransactionMessageView.vue";
import TransactionInput from "./TransactionInput.vue";
import TransactionConfig from "./TransactionConfig.vue";
import TransactionAttempts from "./TransactionAttempts.vue";
import { configFields } from "../utils/serviceConfig";
import {
  elapsed,
  formatMs,
  formatTimestamp,
  statusBadge,
  statusCodeBadge,
  statusLabel,
} from "../utils/transactionFormatters";
import { getRunStatusBadgeClass, getRunStatusLabel } from "../utils/runFormatters";

const props = defineProps<{
  runId: string;
  serviceId: string;
  detail: TransactionDetail;
  canPrev: boolean;
  canNext: boolean;
  /** Shown beside the list, which does the stepping and the going back. */
  split?: boolean;
  /** This service in the version the run pinned. Undefined while it loads or when the read failed. */
  service?: Service;
  /** Service names by id. An id missing here is shown as the id. */
  serviceNames?: Record<string, string>;
}>();
const emit = defineEmits<{ back: []; prev: []; next: [] }>();

const execution = computed(() => props.detail.execution);
const result = computed(() => props.detail.result);
// The authored kind tells a paged call from a plain one, which share the HTTP execution.
const kind = computed(() => props.service?.serviceModelType.$type ?? execution.value.$type);
// The attempt that settled the transaction. Only an HTTP execution makes attempts.
const settlingAttempt = computed(() => (execution.value.$type === "http" ? (execution.value.last ?? undefined) : undefined));
const subWorkflow = computed(() => (execution.value.$type === "subWorkflow" ? execution.value : undefined));
const sftp = computed(() => (execution.value.$type === "sftp" ? execution.value : undefined));

// Which rows are open. Result is what a reader opens a transaction for; every other row is one click away.
const open = ref<Record<string, boolean>>({ Result: true });
function toggle(row: string) {
  open.value[row] = !open.value[row];
}

// --- Summary ---------------------------------------------------------------

// From creation to completion. A transaction completed before the completed time was stamped falls back to
// the settling attempt's answer, or to the compute time for a kind that makes no HTTP request.
const duration = computed(() => {
  const detail = props.detail;
  if (detail.completedAt) return elapsed(detail.created, detail.completedAt);
  if (execution.value.$type !== "http") return formatMs(detail.cost.computeMs);
  const attempt = settlingAttempt.value;
  if (!attempt) return "";
  return elapsed(detail.created, attempt.receivedAt ?? attempt.sentAt);
});

// How many times the transaction was tried. A plain HTTP call counts its requests on the wire. A paged call's
// requests are its pages, and a kind with no HTTP record makes one try per execution, so both count executions.
const attempts = computed(() => {
  if (execution.value.$type === "http" && kind.value !== "httpPaging") return execution.value.attemptCount;
  return props.detail.attemptCount;
});

// The detail carries no validation for the default rule's Proceed, so any rule here matched for a reason.
const ruleLine = computed(() => {
  const validation = props.detail.validation;
  if (!validation || validation.action === "Proceed") return "";
  const parts = [validation.ruleName, validation.action, validation.message];
  return parts.filter((part) => part !== "").join(" · ");
});
const failed = computed(() => props.detail.status === "Failed");

// --- Config ----------------------------------------------------------------

// Nothing until the pinned services arrive, so the row stays out while they load or when the read failed.
const config = computed(() => (props.service ? configFields(props.service, props.serviceNames ?? {}) : []));

// --- Result ----------------------------------------------------------------

// Which answer Result shows. A callback replaced the attempt's direct answer. A paged call's result is one list
// with a page per element. A plain HTTP call's result is the settling attempt's response, even when the
// transaction failed and stored no result of its own.
type ResultCase = "callback" | "pages" | "attempt" | "other";
const resultCase = computed<ResultCase>(() => {
  if (result.value.inbound) return "callback";
  if (kind.value === "httpPaging" && result.value.elements != null) return "pages";
  if (settlingAttempt.value?.response && !result.value.fanOut) return "attempt";
  return "other";
});

// An attempt that failed before any response arrived.
const attemptError = computed(() =>
  settlingAttempt.value?.response ? undefined : (settlingAttempt.value?.error ?? undefined),
);

const resultIsEmpty = computed(
  () =>
    resultCase.value === "other" &&
    result.value.statusCode == null &&
    result.value.headers.length === 0 &&
    result.value.body.outcome === "None" &&
    !attemptError.value,
);

const settlingAttemptDuration = computed(() => {
  const attempt = settlingAttempt.value;
  if (!attempt) return "";
  return elapsed(attempt.sentAt, attempt.receivedAt);
});

// How long the callback took to arrive after the attempt's direct answer.
const callbackWait = computed(() => {
  const receivedAt = settlingAttempt.value?.receivedAt;
  if (!result.value.inbound || !receivedAt) return "";
  return elapsed(receivedAt, props.detail.completedAt);
});

const elementCount = computed(() => result.value.elements ?? 0);

const resultFigure = computed(() => {
  switch (resultCase.value) {
    case "callback":
      return callbackWait.value;
    case "pages":
      return `${elementCount.value.toLocaleString()} pages`;
    case "attempt":
      return settlingAttemptDuration.value;
    case "other": {
      if (result.value.elements != null) return `${result.value.elements.toLocaleString()} elements`;
      const outputs = props.detail.outputs.count;
      if (result.value.fanOut && outputs != null) return `${outputs.toLocaleString()} elements`;
      return "";
    }
  }
});

// Callback and paged results always list their headers; any other result lists them only when there are some.
const showResultHeaders = computed(() => resultCase.value !== "other" || result.value.headers.length > 0);
const headersOpen = ref(false);

// A paged result is read one element at a time. The read remembers which element it holds, so the body and
// its source stay paired while the next element loads.
const fetchBody = useTransactionBody();
const element = ref(0);
const elementBody = ref<{ index: number; body: ContentPreview }>();
const elementFetching = ref(false);
const elementFailed = ref(false);
// Counts reads, so a read that answers after the pager moved on is dropped rather than shown.
let elementRead = 0;

async function readElement() {
  const thisRead = ++elementRead;
  const index = element.value;
  elementFetching.value = true;
  elementFailed.value = false;
  try {
    const body = await fetchBody(props.runId, props.serviceId, props.detail.id, { element: index });
    if (thisRead !== elementRead) return;
    elementBody.value = { index, body };
  } catch {
    if (thisRead !== elementRead) return;
    elementFailed.value = true;
  } finally {
    if (thisRead === elementRead) elementFetching.value = false;
  }
}

// The kind is known once the pinned services arrive, which may be after this view opened.
watch(
  [element, resultCase],
  () => {
    if (resultCase.value === "pages") readElement();
  },
  { immediate: true },
);

// --- Workflow --------------------------------------------------------------

const childRunDuration = computed(() => {
  const child = subWorkflow.value;
  if (!child?.startedAt) return "";
  return elapsed(child.startedAt, child.completedAt);
});

// --- Attempts and Pages ----------------------------------------------------

// How many requests an HTTP transaction put on the wire. Zero for a kind with no HTTP record.
const httpAttemptCount = computed(() => (execution.value.$type === "http" ? execution.value.attemptCount : 0));
// One attempt says nothing Request and Result do not. A paged call makes one per page, so it always lists them.
const showAttempts = computed(
  () => execution.value.$type === "http" && (httpAttemptCount.value > 1 || kind.value === "httpPaging"),
);

// --- Details ---------------------------------------------------------------

// A transaction that never began an execution carries .NET's default date, 0001-01-01.
const executionBegan = computed(() => new Date(props.detail.executionStartedAt).getUTCFullYear() > 1);

const rateLimitWaitMs = computed(() => {
  const cost = props.detail.cost;
  return cost.throttledVendorMs + cost.throttledInternalMs + cost.throttledOrgMs + cost.throttledPlatformMs;
});
</script>

<template>
  <div>
    <div v-if="!split" class="d-flex align-items-center mb-3">
      <button type="button" class="btn btn-sm btn-link p-0" @click="emit('back')">
        <i class="bi bi-arrow-left me-1"></i>Transactions
      </button>
      <div class="btn-group btn-group-sm ms-auto" role="group" aria-label="Previous or next transaction">
        <button
          type="button"
          class="btn btn-outline-secondary"
          title="Previous transaction"
          :disabled="!canPrev"
          @click="emit('prev')"
        >
          <i class="bi bi-chevron-up"></i>
        </button>
        <button
          type="button"
          class="btn btn-outline-secondary"
          title="Next transaction"
          :disabled="!canNext"
          @click="emit('next')"
        >
          <i class="bi bi-chevron-down"></i>
        </button>
      </div>
    </div>

    <dl class="row mb-3 small">
      <TransactionField label="Status">
        <span class="badge" :class="statusBadge(detail.status)">{{ statusLabel(detail.status) }}</span>
      </TransactionField>
      <TransactionField v-if="duration" label="Duration">{{ duration }}</TransactionField>
      <TransactionField label="Created">{{ formatTimestamp(detail.created) }}</TransactionField>
      <TransactionField v-if="attempts > 1" label="Attempts">{{ attempts }}</TransactionField>
      <TransactionField v-if="ruleLine" label="Validator">
        <span :class="{ 'text-danger': failed }">{{ ruleLine }}</span>
      </TransactionField>
      <TransactionField v-else-if="detail.error" label="Error">
        <span class="text-danger">{{ detail.error.message }}</span>
      </TransactionField>
    </dl>

    <TransactionBlock v-if="detail.parents.length" label="Input" :open="!!open.Input" @toggle="toggle('Input')">
      <TransactionInput
        :parents="detail.parents"
        :service-names="serviceNames ?? {}"
        :run-id="runId"
      />
    </TransactionBlock>

    <TransactionBlock v-if="config.length" label="Config" :open="!!open.Config" @toggle="toggle('Config')">
      <TransactionConfig :fields="config" />
    </TransactionBlock>

    <TransactionBlock
      v-if="settlingAttempt || sftp"
      label="Request"
      :open="!!open.Request"
      @toggle="toggle('Request')"
    >
      <dl class="row mb-0 small">
        <TransactionMessageView
          v-if="settlingAttempt"
          kind="request"
          :message="settlingAttempt.request"
          :attempt-id="settlingAttempt.id"
          :run-id="runId"
          :service-id="serviceId"
          :transaction-id="detail.id"
        />
        <template v-else-if="sftp">
          <TransactionField label="Operation">{{ sftp.operation }}</TransactionField>
          <TransactionField label="Remote path"><span class="font-monospace">{{ sftp.remotePath }}</span></TransactionField>
          <TransactionField v-if="sftp.remoteFileName" label="Remote file name">
            <span class="font-monospace">{{ sftp.remoteFileName }}</span>
          </TransactionField>
          <TransactionField v-if="sftp.newPath" label="New path">
            <span class="font-monospace">{{ sftp.newPath }}</span>
          </TransactionField>
          <TransactionField v-if="sftp.overwrite" label="Overwrite">Yes</TransactionField>
          <TransactionField label="Started">{{ formatTimestamp(sftp.startedAt) }}</TransactionField>
        </template>
      </dl>
    </TransactionBlock>

    <TransactionBlock
      v-if="subWorkflow"
      label="Workflow"
      :open="!!open.Workflow"
      :figure="childRunDuration"
      @toggle="toggle('Workflow')"
    >
      <dl class="row mb-0 small">
        <TransactionField label="Workflow">{{ subWorkflow.workflowName ?? subWorkflow.workflowId }}</TransactionField>
        <TransactionField v-if="subWorkflow.status" label="Status">
          <span class="badge" :class="getRunStatusBadgeClass(subWorkflow.status)">
            {{ getRunStatusLabel(subWorkflow.status) }}
          </span>
        </TransactionField>
        <TransactionField v-if="subWorkflow.startedAt" label="Started">
          {{ formatTimestamp(subWorkflow.startedAt) }}
        </TransactionField>
        <TransactionField label="Run">
          <RouterLink
            :to="{ name: 'global-runs-run', params: { workflowid: subWorkflow.workflowId, runid: subWorkflow.childRunId } }"
          >
            Open the child run
          </RouterLink>
        </TransactionField>
      </dl>
    </TransactionBlock>

    <TransactionBlock
      v-if="result.inbound && settlingAttempt?.response"
      label="Response"
      :open="!!open.Response"
      :figure="settlingAttemptDuration"
      @toggle="toggle('Response')"
    >
      <dl class="row mb-0 small">
        <TransactionMessageView
          kind="response"
          :message="settlingAttempt.response"
          :attempt-id="settlingAttempt.id"
          :run-id="runId"
          :service-id="serviceId"
          :transaction-id="detail.id"
        />
      </dl>
    </TransactionBlock>

    <TransactionBlock
      v-if="!resultIsEmpty"
      label="Result"
      :open="!!open.Result"
      :figure="resultFigure"
      @toggle="toggle('Result')"
    >
      <dl class="row mb-0 small">
        <TransactionMessageView
          v-if="resultCase === 'attempt' && settlingAttempt?.response"
          kind="response"
          :message="settlingAttempt.response"
          :attempt-id="settlingAttempt.id"
          :run-id="runId"
          :service-id="serviceId"
          :transaction-id="detail.id"
        />
        <template v-else>
          <template v-if="resultCase === 'callback'">
            <TransactionField label="Source">Callback</TransactionField>
            <TransactionField v-if="detail.completedAt" label="Received">
              {{ formatTimestamp(detail.completedAt) }}
            </TransactionField>
          </template>
          <TransactionField v-else-if="result.statusCode != null" label="Status">
            <span class="badge" :class="statusCodeBadge(result.statusCode)">{{ result.statusCode }}</span>
          </TransactionField>

          <TransactionField v-if="attemptError" label="Error">
            <span class="text-danger">{{ attemptError.message }}</span>
          </TransactionField>

          <TransactionField v-if="showResultHeaders" label="Headers">
            <span v-if="!result.headers.length" class="text-body-secondary">None</span>
            <template v-else>
              <button
                type="button"
                class="btn btn-link btn-sm p-0 align-baseline"
                :aria-expanded="headersOpen"
                @click="headersOpen = !headersOpen"
              >
                {{ headersOpen ? "Hide" : "Show" }}
              </button>
              <table v-if="headersOpen" class="table table-sm table-borderless mt-1 mb-0">
                <tbody>
                  <tr v-for="header in result.headers" :key="header.name">
                    <td class="text-body-secondary text-nowrap ps-0 pe-3">{{ header.name }}</td>
                    <td class="font-monospace px-0">{{ header.value }}</td>
                  </tr>
                </tbody>
              </table>
            </template>
          </TransactionField>

          <TransactionField label="Body">
            <template v-if="resultCase === 'pages'">
              <span v-if="elementFailed">
                <span class="text-danger">Couldn't load the page.</span>
                <button type="button" class="btn btn-link btn-sm p-0 ms-1 align-baseline" @click="readElement">
                  Retry
                </button>
              </span>
              <TransactionBody
                v-else-if="elementBody"
                :body="elementBody.body"
                :source="{ element: elementBody.index }"
                :run-id="runId"
                :service-id="serviceId"
                :transaction-id="detail.id"
              />
              <span v-else class="text-body-secondary">Loading…</span>
              <KeysetPager
                :label="`Page ${element + 1} of ${elementCount}`"
                :can-prev="element > 0"
                :can-next="element < elementCount - 1"
                :fetching="elementFetching"
                @prev="element--"
                @next="element++"
              />
            </template>
            <TransactionBody
              v-else
              :body="result.body"
              source="result"
              :run-id="runId"
              :service-id="serviceId"
              :transaction-id="detail.id"
            />
          </TransactionField>
        </template>
      </dl>
    </TransactionBlock>

    <TransactionBlock
      v-if="showAttempts"
      :label="kind === 'httpPaging' ? 'Pages' : 'Attempts'"
      :open="!!open.Attempts"
      :figure="String(httpAttemptCount)"
      @toggle="toggle('Attempts')"
    >
      <TransactionAttempts
        :run-id="runId"
        :service-id="serviceId"
        :transaction-id="detail.id"
        :total="httpAttemptCount"
      />
    </TransactionBlock>

    <TransactionBlock label="Details" :open="!!open.Details" @toggle="toggle('Details')">
      <dl class="row mb-0 small">
        <TransactionField label="Run"><span class="font-monospace">{{ detail.runId }}</span></TransactionField>
        <TransactionField label="Transaction"><span class="font-monospace">{{ detail.id }}</span></TransactionField>
        <TransactionField v-if="detail.connection" label="Connection">
          <RouterLink :to="{ name: 'connection-detail', params: { connectionid: detail.connection.id } }">
            {{ detail.connection.name ?? detail.connection.id }}
          </RouterLink>
        </TransactionField>
      </dl>
      <dl class="row mb-0 mt-2 small">
        <TransactionField label="Created">{{ formatTimestamp(detail.created) }}</TransactionField>
        <TransactionField v-if="executionBegan" label="Started">
          {{ formatTimestamp(detail.executionStartedAt) }}
        </TransactionField>
        <TransactionField v-if="detail.completedAt" label="Completed">{{ formatTimestamp(detail.completedAt) }}</TransactionField>
        <TransactionField v-if="rateLimitWaitMs > 0" label="Rate-limit wait">{{ formatMs(rateLimitWaitMs) }}</TransactionField>
        <TransactionField v-if="callbackWait" label="Waited for callback">{{ callbackWait }}</TransactionField>
        <TransactionField v-if="detail.cost.vendorCalls > 0" label="Vendor calls">{{ detail.cost.vendorCalls }}</TransactionField>
      </dl>
    </TransactionBlock>
  </div>
</template>
